
import React, { useRef, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { MessageCircle } from 'lucide-react';

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  created_at: string;
  sender_name?: string;
  sender_avatar?: string;
}

interface MessagesListProps {
  messages: Message[];
  currentUserId?: string;
}

const MessagesList: React.FC<MessagesListProps> = ({ messages, currentUserId }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (messages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <MessageCircle className="h-16 w-16 mx-auto mb-4 text-primary/30" />
          <p className="text-muted-foreground font-medium">No messages yet</p>
          <p className="text-sm text-muted-foreground">Start a conversation! 💬</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 overflow-y-auto bg-gradient-to-b from-background/50 to-background/30">
      <div className="space-y-4">
        {messages.map((msg) => (
          <div 
            key={msg.id} 
            className={`flex ${msg.sender_id === currentUserId ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`flex items-end max-w-xs lg:max-w-md ${msg.sender_id === currentUserId ? 'flex-row-reverse' : 'flex-row'}`}>
              <Avatar className="h-8 w-8 mx-2">
                <AvatarImage src={msg.sender_avatar} />
                <AvatarFallback className="text-xs">{msg.sender_name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <div 
                  className={`
                    px-4 py-2 rounded-2xl shadow-sm
                    ${msg.sender_id === currentUserId 
                      ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground' 
                      : 'bg-card border border-border'}
                  `}
                >
                  <p className="text-sm">{msg.content}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1 px-1">
                  {formatMessageTime(msg.created_at)}
                </p>
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessagesList;
